import { useCallback, useEffect, useState } from "react";
import { HomeScreen } from "./screens/HomeScreen";
import { NewIdeaScreen } from "./screens/NewIdeaScreen";
import { IdeaDetailScreen } from "./screens/IdeaDetailScreen";
import { SettingsScreen } from "./screens/SettingsScreen";
import { ModelDownloadScreen } from "./screens/ModelDownloadScreen";
import { DebugScreen } from "./screens/DebugScreen";
import { ClarifyScreen } from "./screens/ClarifyScreen";
import { ScanScreen } from "./screens/ScanScreen";
import { PersonasScreen } from "./screens/PersonasScreen";
import { BuildSpecScreen } from "./screens/BuildSpecScreen";
import { listIdeas, putIdea, deleteIdea, getIdea } from "./data/ideaStore";
import type { Idea, Settings } from "./data/types";
import { effectiveModelConfig, newIdea } from "./data/types";
import { loadSettings } from "./data/settingsStore";
import { getInstalledModel, type ModelInfo } from "./data/modelStore";

type Route =
  | { kind: "home" }
  | { kind: "new" }
  | { kind: "detail"; ideaId: string }
  | { kind: "clarify"; ideaId: string }
  | { kind: "scan"; ideaId: string }
  | { kind: "personas"; ideaId: string }
  | { kind: "buildSpec"; ideaId: string }
  | { kind: "settings" }
  | { kind: "modelDownload" }
  | { kind: "debug" };

export function App() {
  const [route, setRoute] = useState<Route>({ kind: "home" });
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [settings, setSettings] = useState<Settings>(() => loadSettings());
  const [installedModel, setInstalledModel] = useState<ModelInfo | null>(null);

  const refreshIdeas = useCallback(async () => {
    setIdeas(await listIdeas());
  }, []);

  const refreshInstalledModel = useCallback(async () => {
    const fileName = effectiveModelConfig(settings).fileName;
    setInstalledModel(await getInstalledModel(fileName));
  }, [settings]);

  useEffect(() => {
    void refreshIdeas();
  }, [refreshIdeas]);

  useEffect(() => {
    void refreshInstalledModel();
  }, [refreshInstalledModel]);

  const handleSaveNewIdea = useCallback(
    async (title: string, rawCapture: string) => {
      const created = newIdea({ title, rawCapture });
      await putIdea(created);
      await refreshIdeas();
      setRoute({ kind: "detail", ideaId: created.id });
    },
    [refreshIdeas],
  );

  const handleDeleteIdea = useCallback(
    async (id: string) => {
      await deleteIdea(id);
      await refreshIdeas();
      setRoute({ kind: "home" });
    },
    [refreshIdeas],
  );

  return (
    <div className="mx-auto flex min-h-full max-w-3xl flex-col">
      {route.kind === "home" && (
        <HomeScreen
          ideas={ideas}
          settings={settings}
          installedModel={installedModel}
          onNewIdea={() => setRoute({ kind: "new" })}
          onIdeaClick={(idea) => setRoute({ kind: "detail", ideaId: idea.id })}
          onOpenSettings={() => setRoute({ kind: "settings" })}
        />
      )}

      {route.kind === "new" && (
        <NewIdeaScreen
          onCancel={() => setRoute({ kind: "home" })}
          onSave={handleSaveNewIdea}
        />
      )}

      {route.kind === "detail" && (
        <IdeaDetailRoute
          ideaId={route.ideaId}
          onBack={() => setRoute({ kind: "home" })}
          onDelete={handleDeleteIdea}
          onClarify={(ideaId) => setRoute({ kind: "clarify", ideaId })}
          onScan={(ideaId) => setRoute({ kind: "scan", ideaId })}
          onPersonas={(ideaId) => setRoute({ kind: "personas", ideaId })}
          onBuildSpec={(ideaId) => setRoute({ kind: "buildSpec", ideaId })}
        />
      )}

      {route.kind === "clarify" && (
        <ClarifyScreen
          ideaId={route.ideaId}
          settings={settings}
          installedModel={installedModel}
          onBack={() => setRoute({ kind: "detail", ideaId: route.ideaId })}
          onIdeaSaved={() => {
            void refreshIdeas();
          }}
        />
      )}

      {route.kind === "scan" && (
        <ScanScreen
          ideaId={route.ideaId}
          settings={settings}
          installedModel={installedModel}
          onBack={() => setRoute({ kind: "detail", ideaId: route.ideaId })}
          onIdeaSaved={() => {
            void refreshIdeas();
          }}
        />
      )}

      {route.kind === "personas" && (
        <PersonasScreen
          ideaId={route.ideaId}
          settings={settings}
          installedModel={installedModel}
          onBack={() => setRoute({ kind: "detail", ideaId: route.ideaId })}
          onIdeaSaved={() => {
            void refreshIdeas();
          }}
        />
      )}

      {route.kind === "buildSpec" && (
        <BuildSpecScreen
          ideaId={route.ideaId}
          settings={settings}
          installedModel={installedModel}
          onBack={() => setRoute({ kind: "detail", ideaId: route.ideaId })}
          onIdeaSaved={() => {
            void refreshIdeas();
          }}
        />
      )}

      {route.kind === "settings" && (
        <SettingsScreen
          settings={settings}
          installedModel={installedModel}
          onChange={setSettings}
          onBack={() => setRoute({ kind: "home" })}
          onOpenModel={() => setRoute({ kind: "modelDownload" })}
          onOpenDebug={() => setRoute({ kind: "debug" })}
        />
      )}

      {route.kind === "debug" && (
        <DebugScreen settings={settings} onBack={() => setRoute({ kind: "settings" })} />
      )}

      {route.kind === "modelDownload" && (
        <ModelDownloadScreen
          settings={settings}
          onChange={setSettings}
          onBack={() => setRoute({ kind: "settings" })}
          onInstalledChange={() => {
            void refreshInstalledModel();
          }}
        />
      )}
    </div>
  );
}

function IdeaDetailRoute(props: {
  ideaId: string;
  onBack: () => void;
  onDelete: (id: string) => void;
  onClarify: (ideaId: string) => void;
  onScan: (ideaId: string) => void;
  onPersonas: (ideaId: string) => void;
  onBuildSpec: (ideaId: string) => void;
}) {
  const [idea, setIdea] = useState<Idea | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void getIdea(props.ideaId).then((found) => {
      if (cancelled) return;
      setIdea(found);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [props.ideaId]);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center text-ink-muted">Loading…</div>
    );
  }
  if (!idea) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <p>This idea was deleted.</p>
        <button className="btn-ghost" onClick={props.onBack}>
          Back to home
        </button>
      </div>
    );
  }
  return (
    <IdeaDetailScreen
      idea={idea}
      onBack={props.onBack}
      onDelete={() => props.onDelete(idea.id)}
      onClarify={() => props.onClarify(idea.id)}
      onScan={() => props.onScan(idea.id)}
      onPersonas={() => props.onPersonas(idea.id)}
      onBuildSpec={() => props.onBuildSpec(idea.id)}
    />
  );
}
